/**
 * 위치 고르기 — 등록된 위치 중에서 고르거나, 없으면 새로 적는다.
 *
 * 예전에는 어디서나 자유 텍스트였다. 그래서 같은 지점이 "본사1층"·"본사 1층"·
 * "본사 1층 로비" 로 갈라졌고, 모니터링 벽이 위치로 묶이기 시작하면서 그 갈라짐이
 * 그대로 **벽의 구획이 셋으로 쪼개지는 것**으로 드러났다.
 *
 * 그렇다고 목록에 있는 것만 고르게 할 수는 없다. 첫 화면을 붙이는 순간에는 목록이
 * 비어 있고, 새 지점은 언제나 생긴다. **고르기를 기본으로 하되 적는 길을 남긴다.**
 *
 * **늘 드롭다운으로 연다 — 등록된 위치가 하나도 없어도.** 적는 칸은 [새 위치 직접 입력…] 을 골랐을 때만 나온다.
 * 예전에는 고를 것이 없으면 처음부터 적는 칸을 내밀었는데, 위치를 아직 아무도 적지 않은 운영 서버에서는 그것이
 * 화면 추가·화면 상세·일괄 변경 어디서나 "위치는 자유 입력" 으로 보였다(2026-10-19 사용자 지시).
 */
import { useState, type ReactNode } from 'react';
import { isNewLocation, locationChoices, NEW_LOCATION } from './locationOptions.js';

export function LocationPicker({
  value,
  known,
  disabled = false,
  ariaLabel = '위치',
  /** 입력 중 Enter. 저장 버튼이 따로 있는 화면에서 손을 떼지 않고 저장하게 한다. */
  onSubmit,
  onChange,
}: {
  value: string;
  /** 서버가 준 "지금 쓰이고 있는 위치" 이름들. */
  known: readonly string[];
  disabled?: boolean;
  ariaLabel?: string;
  onSubmit?: () => void;
  onChange: (next: string) => void;
}): ReactNode {
  const choices = locationChoices(known, value);

  /**
   * 적기로 넘어갔는가 — 사람이 [새 위치 직접 입력…] 을 골랐을 때만 참이다.
   *
   * 목록의 길이로 정하지 않는다. 위치 목록은 서버에서 비동기로 오므로 첫 렌더에서는 언제나 비어 있고, 그것으로
   * 정하면 목록이 도착하기 전 한순간 적는 칸이 떴다 사라진다(예전에는 초기값으로 두어 그 칸에 갇히기까지 했다).
   */
  const [typing, setTyping] = useState(false);

  if (typing) {
    return (
      <span className="location-picker">
        <input
          className="input"
          value={value}
          placeholder="예: 본사 1층 로비"
          aria-label={ariaLabel}
          maxLength={200}
          disabled={disabled}
          // 사람이 방금 [새 위치 직접 입력…] 을 골라서 나온 칸이다 — 곧바로 적게 한다.
          autoFocus
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') onSubmit?.();
          }}
        />
        {/*
          되돌아갈 길을 남긴다. 적기로 들어왔다가 마음이 바뀌었을 때 화면을 나갔다
          다시 들어오게 하면, 그 사이에 적던 것이 사라진다. 등록된 위치가 없어도 둔다 —
          드롭다운으로 돌아가 [위치 미지정] 을 고르는 길이기도 하다.
        */}
        <button
          type="button"
          className="btn btn-tiny"
          disabled={disabled}
          onClick={() => setTyping(false)}
          title="등록된 위치 중에서 고릅니다"
        >
          목록에서 선택
        </button>
      </span>
    );
  }

  return (
    <span className="location-picker">
      <select
        className="input"
        value={value}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(event) => {
          const next = event.target.value;
          /*
            **"직접 입력" 은 위치를 지운 것이 아니다.**

            여기서 값을 빈 문자열로 바꿔 버리면, 적으려고 누른 사람의 기존 위치가
            그 순간 사라진다. 모드만 바꾸고 값은 그대로 둔다 — 지우고 싶으면
            입력칸에서 지우면 된다.
          */
          if (isNewLocation(next)) {
            setTyping(true);
            return;
          }
          onChange(next);
        }}
      >
        <option value="">위치 미지정</option>
        {choices.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
        <option value={NEW_LOCATION}>새 위치 직접 입력…</option>
      </select>
    </span>
  );
}
